import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 13000-13999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const jiraErrors = {
  // Live at 4 sites in jira.service.ts (identical text). No confirmed frontend consumer found (the
  // "Ticket not found" text also appears at several RESOURCE_NOT_FOUND-coded sites in the same file
  // — those are a different, out-of-scope code, not part of this entry).
  JIRA_TICKET_NOT_FOUND: {
    errorCode: 13001,
    errorMessage: t("Ticket not found"),
    isDynamic: false,
  },
  // Live at jira.service.ts. Confirmed safe — jiraTicketDetails.tsx branches on this exact code to
  // render its own access-denied illustration, never reads errorMessage.
  JIRA_TICKET_NO_ACCESS: {
    errorCode: 13002,
    errorMessage: t("You do not have access to this ticket"),
    isDynamic: false,
  },
  // 13003-13009 is the jira module's SOMETHING_WENT_WRONG (9000) / RESOURCE_NOT_FOUND (9003) split.
  // Five sites reused JIRA_TICKET_NOT_FOUND (13001) verbatim; two stay generic.
  // 13003-13009 — the jira module's split.
  JIRA_INCORRECT_USER: {
    errorCode: 13003,
    errorMessage: t("Incorrect user"),
    isDynamic: false,
  },
  JIRA_ISSUE_CREATE_FAILED: {
    errorCode: 13004,
    errorMessage: t("Issue could not be created"),
    isDynamic: false,
  },
  JIRA_TICKET_SLA_NOT_TRACKED: {
    errorCode: 13005,
    errorMessage: t("SLA is not tracked for this ticket"),
    isDynamic: false,
  },
  // A deliberate mask: gone vs. not-yours are answered identically. Distinct from
  // JIRA_TICKET_NOT_FOUND (13001), which is the unmasked not-found.
  JIRA_TICKET_NOT_FOUND_OR_NO_ACCESS: {
    errorCode: 13006,
    errorMessage: t("Ticket not found or insufficient access"),
    isDynamic: false,
  },
  JIRA_ATTACHMENTS_UPLOAD_FAILED: {
    errorCode: 13007,
    errorMessage: t("Attachments cannot be uploaded"),
    isDynamic: false,
  },
  JIRA_COMMENT_NOT_FOUND: {
    errorCode: 13008,
    errorMessage: t("Comment not found"),
    isDynamic: false,
  },
  // Deliberately not JIRA_TICKET_NO_ACCESS (13002), whose sentence names the ticket explicitly.
  JIRA_INSUFFICIENT_ACCESS: {
    errorCode: 13009,
    errorMessage: t("Insufficient access"),
    isDynamic: false,
  },
  // A genuine permission refusal, but with its own sentence naming internal notes. 2 sites.
  JIRA_INTERNAL_NOTES_NO_PERMISSION: {
    errorCode: 13010,
    errorMessage: t("Insufficient permission to add internal notes"),
    isDynamic: false,
  },
  // 13011-13016 came off VALIDATION_FAILED (9004).
  JIRA_STATUS_TRANSITION_INVALID: {
    errorCode: 13011,
    errorMessage: t("Invalid status transition for this ticket"),
    isDynamic: false,
  },
  JIRA_SEVERITY_CHANGE_NEEDS_COMMENT: {
    errorCode: 13012,
    errorMessage: t("A justification comment is required when changing severity"),
    isDynamic: false,
  },
  JIRA_STATUS_INVALID_FOR_TYPE: {
    errorCode: 13013,
    errorMessage: t("Invalid status for the selected ticket type"),
    isDynamic: false,
  },
  JIRA_IMPACT_REQUIRED_FOR_BUG: {
    errorCode: 13014,
    errorMessage: t("Impact is required when changing type to Report a Bug"),
    isDynamic: false,
  },
  JIRA_TICKET_DATA_REQUIRED: {
    errorCode: 13015,
    errorMessage: t("Ticket data is required"),
    isDynamic: false,
  },
  JIRA_COMMENT_ID_REQUIRED: {
    errorCode: 13016,
    errorMessage: t("commentId is required"),
    isDynamic: false,
  },
  // 13017 arrived with staging's SLA Report Export (CUBE-29443). (13018 held
  // JIRA_PORTAL_NOT_RESOLVED, removed when staging replaced that guard with tenant derivation;
  // the number is free.)
  // The reader's own reason travels in `params.message` — it names WHICH field of the stored report
  // failed to parse, which is the only thing that makes the refusal actionable.
  JIRA_INFRA_SLA_REPORT_INVALID: {
    errorCode: 13017,
    errorMessage: t("{{message}}"),
    isDynamic: true,
    dynamicParams: ["message"],
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
